/**
 * The Buchhaltung report's vocabulary (CHG-2026-09-tax-agency-report, CHG-2026-10-accounting-close-periods).
 *
 * The endpoint returns keys, not words — `BAR`, `STAFF`, `AT4_9` — and the two server-rendered
 * exports carry their own German copy because a CSV and a PDF leave the browser. The page therefore
 * keeps its own labels here rather than printing what the server sent, so the screen does not
 * depend on export copy for its own words. Both sides say the same thing, deliberately.
 *
 * The channel labels are this report's, **not** the manager report's: `STAFF` is "Bedienung" here
 * and stays "Bedient" there. One report speaks to the controller, the other to the owner, and the
 * spec names the wording for each.
 *
 * A name that came out of the data — a Hauptgruppe, an Entnahme reason — is never translated: it is
 * rendered as it was stored.
 */
type Translate = (key: string, options?: Record<string, unknown>) => string

/**
 * Where a Tagesabschluss window starts: the previous close. A Standort's very first close has none
 * (null) and runs "seit Inbetriebnahme"; an epoch start, as the edge stamps a Kassa's first Beleg,
 * reads the same rather than as 1 January 1970.
 */
export function closeStartLabel(
  t: Translate,
  iso: string | null,
  format: (iso: string) => string,
): string {
  return iso == null || new Date(iso).getUTCFullYear() < 2000
    ? t('accounting.sinceStart')
    : format(iso)
}

/** Payment buckets, in the order the endpoint returns them. */
export const PAYMENT_METHOD_KEYS = [
  'BAR',
  'POS_KARTE',
  'GUTSCHEIN',
  'ONLINE_KARTE',
  'UNBEKANNT',
] as const

export const CHANNEL_KEYS = [
  'SELF_CHECKOUT',
  'STAFF',
  'ONLINE',
  'UNKNOWN',
] as const

/** Canonical rate order, as the Tagesabschluss prints it and the endpoint returns it. */
export const VAT_CODES = ['AT20', 'AT10', 'AT13', 'AT4_9', 'AT0'] as const

/** The sentinel the item facts use for a sale whose article has no product group left. */
export const SPARTE_UNKNOWN_KEY = '__unknown__'

export function paymentMethodLabel(t: Translate, method: string): string {
  return (PAYMENT_METHOD_KEYS as ReadonlyArray<string>).includes(method)
    ? t(`accounting.methods.${method}`)
    : method
}

export function channelLabel(
  t: Translate,
  key: string,
  label: string | null,
): string {
  return (CHANNEL_KEYS as ReadonlyArray<string>).includes(key)
    ? t(`accounting.channels.${key}`)
    : (label ?? key)
}

export function vatRateLabel(t: Translate, vatCode: string): string {
  return (VAT_CODES as ReadonlyArray<string>).includes(vatCode)
    ? t(`accounting.rates.${vatCode}`)
    : vatCode
}

/** A Hauptgruppe keeps its own name; only the "no group at all" bucket gets one from us. */
export function spartenLabel(
  t: Translate,
  key: string,
  label: string | null,
): string {
  if (label && label.trim() !== '') return label
  return key === SPARTE_UNKNOWN_KEY ? t('accounting.spartenUnknown') : key
}
